import {Edit2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import type {SapHcmInboundConfig} from '@/types/inbound-connector'

interface SapHcmInboundConfigCardProps {
    config: SapHcmInboundConfig
    canManage?: boolean
    onEdit?: () => void
}

export function SapHcmInboundConfigCard({config, canManage, onEdit}: SapHcmInboundConfigCardProps) {
    const i18n = useI18n()
    return (
        <Card data-testid="sap-hcm-config-section" data-help-prefix="sapHcmInboundConfigCard">
            <RowCardHeader>
                <div>
                    <CardTitle data-testid="card-title-sap-hcm-config" data-help-key="_section">
                        {i18n.inboundConnectors.sapHcm.connectionSection}
                    </CardTitle>
                    <CardDescription>{i18n.inboundConnectors.sapHcm.connectionDescription}</CardDescription>
                </div>
                {canManage && onEdit && (
                    <Button data-testid="btn-edit-sap-hcm-config" size="sm" variant="outline" onClick={onEdit}>
                        <Icon icon={Edit2} className="mr-2"/>
                        {i18n.common.edit}
                    </Button>
                )}
            </RowCardHeader>
            <CardContent>
                <Stack gap="md">
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.baseUrl} value={<CodeText>{config.baseUrl}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.sapHcm.employeeKeyField}
                                     value={<CodeText data-testid="text-sap-hcm-employee-key-field">{config.employeeKeyField}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.sapHcm.validityResolutionStrategy}
                                     value={<Badge variant="outline">{config.validityResolutionStrategy}</Badge>}/>
                        <DetailField label={i18n.inboundConnectors.sapHcm.validityStartField}
                                     value={<CodeText>{config.validityStartField}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.sapHcm.validityEndField}
                                     value={<CodeText>{config.validityEndField}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.authType}
                                     value={<Badge variant="outline">{config.authType}</Badge>}/>
                        {config.authType === 'BASIC_AUTH' && (
                            <DetailField label={i18n.inboundConnectors.username} value={config.username || '—'}/>
                        )}
                        {config.authType === 'OAUTH2_CLIENT' && (<>
                            <DetailField label={i18n.inboundConnectors.clientId} value={config.clientId || '—'}/>
                            <DetailField label={i18n.inboundConnectors.tokenUrl} value={<CodeText>{config.tokenUrl || '—'}</CodeText>}/>
                        </>)}
                    </DetailGrid>
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.pageSize} value={String(config.pageSize)}/>
                        <DetailField label={i18n.inboundConnectors.connectTimeout} value={String(config.connectTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.readTimeout} value={String(config.readTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.maxRetries} value={String(config.maxRetries)}/>
                    </DetailGrid>
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.sapHcm.terminationAttribute}
                                     value={<CodeText>{config.terminationAttribute}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.sapHcm.terminationValuesCsv}
                                     value={<CodeText>{config.terminationValuesCsv}</CodeText>}/>
                    </DetailGrid>
                    {config.enabledInfotypes.length > 0 ? (
                        <DetailField
                            label={i18n.inboundConnectors.sapHcm.enabledInfotypes}
                            value={config.enabledInfotypes.map((it) => (
                                <Badge key={it.infotype} variant="outline">{it.infotype} → {it.targetEntityType}</Badge>
                            ))}
                            fullWidth
                        />
                    ) : (
                        <MutedText>{i18n.inboundConnectors.sapHcm.noInfotypes}</MutedText>
                    )}
                </Stack>
            </CardContent>
        </Card>
    )
}
